import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Input, Result, Select, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ReloadOutlined } from '@ant-design/icons';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { apiClient } from '../../api/client';
import { PagedResponse } from '../../api/types';
import { formatDateTime } from '../../utils/format';

type ClaimKind = 'daily-support' | 'rewarded-ads';
interface RewardClaim {
  id: string;
  user_id: string;
  username: string;
  full_name: string;
  claimed_at: string;
  source: string;
  feature: string;
  reward_item: string;
  reward_amount: string;
  coins_credited: string;
  transaction_id: string;
  ad_unit: string;
}

const SOURCE_LABELS: Record<string, string> = {
  free: 'Free',
  reward_ad: 'Rewarded ad',
  ad_removed: 'Remove Ads entitlement',
};

const filterDate = (value: string, nextDay = false) => {
  if (!value) return undefined;

  const date = new Date(`${value}T00:00:00Z`);
  if (nextDay) date.setUTCDate(date.getUTCDate() + 1);

  return date.toISOString();
};

const formatAmount = (value: string) => BigInt(value).toLocaleString('vi-VN');

export const RewardHistoryPage: React.FC<{ kind: ClaimKind }> = ({ kind }) => {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [userID, setUserID] = useState('');
  const [source, setSource] = useState<string>();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const invalidRange = Boolean(from && to && from > to);
  const invalidUser = Boolean(userID && !/^[1-9]\d{0,18}$/.test(userID));
  const invalidFilters = invalidRange || invalidUser;
  const title = kind === 'daily-support' ? 'Daily Support History' : 'Rewarded Ads Claim History';
  const list = useQuery({
    queryKey: ['admin-reward-claims', kind, page, perPage, search, userID, source, from, to],
    queryFn: async () => {
      const response = await apiClient.get<PagedResponse<RewardClaim>>(`/api/v1/admin/reward-claims/${kind}`, {
        params: {
          page,
          per_page: perPage,
          search: search || undefined,
          user_id: userID || undefined,
          source,
          date_from: filterDate(from),
          date_to: filterDate(to, true),
        },
      });

      return response.data;
    },
    placeholderData: keepPreviousData,
    enabled: !invalidFilters,
  });

  const columns: ColumnsType<RewardClaim> = [
    { title: 'Claim ID', dataIndex: 'id' },
    {
      title: 'User ID',
      dataIndex: 'user_id',
      render: (value: string) => <Link to={`/users/${value}`}>{value}</Link>,
    },
    {
      title: 'Name',
      key: 'name',
      render: (_, row) => <Link to={`/users/${row.user_id}`}>{row.full_name || row.username}</Link>,
    },
    {
      title: 'Source',
      dataIndex: 'source',
      render: (value: string) => <Tag>{SOURCE_LABELS[value] || value}</Tag>,
    },
    { title: 'Feature', dataIndex: 'feature' },
    { title: 'Reward item', dataIndex: 'reward_item' },
    {
      title: 'Reward amount',
      dataIndex: 'reward_amount',
      render: formatAmount,
    },
    {
      title: 'Coins credited',
      dataIndex: 'coins_credited',
      render: formatAmount,
    },
    {
      title: 'Ad transaction ID',
      dataIndex: 'transaction_id',
      render: (value: string) => value || '—',
    },
    {
      title: 'Ad unit',
      dataIndex: 'ad_unit',
      render: (value: string) => value || '—',
    },
    { title: 'Claimed at', dataIndex: 'claimed_at', render: formatDateTime },
  ];

  return (
    <>
      <Typography.Title level={4}>{title}</Typography.Title>
      <Typography.Paragraph type="secondary">
        {kind === 'daily-support'
          ? 'System-wide daily support claims, including free rewards, rewarded ads, and Remove Ads entitlement credits.'
          : 'Committed reward receipts across all features. Remove Ads entitlement credits are labeled separately; rejected callbacks are excluded.'}
      </Typography.Paragraph>
      <div className="mb-4 grid grid-cols-1 items-end gap-3 md:grid-cols-2 xl:grid-cols-3">
        <div className="min-w-0">
          <label htmlFor="claim-search" className="mb-1 block">
            Search
          </label>
          <Input.Search
            id="claim-search"
            aria-label="Search reward claims"
            placeholder="Search claim ID, name, feature, or ad transaction ID"
            maxLength={255}
            allowClear
            value={searchInput}
            onChange={(event) => {
              setSearchInput(event.target.value);
              if (!event.target.value) {
                setSearch('');
                setPage(1);
              }
            }}
            onSearch={(value) => {
              setSearch(value.trim());
              setPage(1);
            }}
            style={{ width: '100%' }}
          />
        </div>
        <div className="min-w-0">
          <label htmlFor="claim-user-id" className="mb-1 block">
            User ID
          </label>
          <Input
            id="claim-user-id"
            aria-label="Filter by user ID"
            placeholder="Exact user ID"
            value={userID}
            allowClear
            inputMode="numeric"
            maxLength={19}
            status={invalidUser ? 'error' : undefined}
            onChange={(event) => {
              setUserID(event.target.value.trim());
              setPage(1);
            }}
          />
        </div>
        <div className="min-w-0">
          <label htmlFor="claim-source" className="mb-1 block">
            Source
          </label>
          <Select
            id="claim-source"
            aria-label="Filter by claim source"
            placeholder="All sources"
            allowClear
            value={source}
            style={{ width: '100%' }}
            options={Object.entries(SOURCE_LABELS)
              .filter(([value]) => kind === 'daily-support' || value !== 'free')
              .map(([value, label]) => ({ value, label }))}
            onChange={(value) => {
              setSource(value);
              setPage(1);
            }}
          />
        </div>
        <label className="flex min-w-0 flex-col gap-1">
          From (UTC)
          <Input
            type="date"
            aria-label="Claims from date (UTC)"
            value={from}
            max={to || undefined}
            onChange={(event) => {
              setFrom(event.target.value);
              setPage(1);
            }}
          />
        </label>
        <label className="flex min-w-0 flex-col gap-1">
          To (UTC, inclusive)
          <Input
            type="date"
            aria-label="Claims to date (UTC, inclusive)"
            value={to}
            min={from || undefined}
            onChange={(event) => {
              setTo(event.target.value);
              setPage(1);
            }}
          />
        </label>
        <Space wrap>
          <Button
            onClick={() => {
              setSearchInput('');
              setSearch('');
              setUserID('');
              setSource(undefined);
              setFrom('');
              setTo('');
              setPage(1);
            }}
          >
            Clear filters
          </Button>
          <Button
            icon={<ReloadOutlined />}
            loading={list.isFetching}
            disabled={invalidFilters}
            onClick={() => list.refetch()}
          >
            Refresh
          </Button>
        </Space>
      </div>
      {invalidRange && <Alert type="error" message="From date must be on or before To date." className="mb-4" />}
      {invalidUser && <Alert type="error" message="User ID must be a positive integer." className="mb-4" />}
      {list.error ? (
        <Result
          status="error"
          title="Failed to load claim history"
          subTitle={list.error instanceof Error ? list.error.message : 'An unexpected error occurred'}
          extra={<Button onClick={() => list.refetch()}>Retry</Button>}
        />
      ) : (
        <Table
          rowKey="id"
          columns={columns}
          dataSource={invalidFilters ? [] : (list.data?.data ?? [])}
          loading={list.isFetching}
          scroll={{ x: 1600 }}
          pagination={{
            current: page,
            pageSize: perPage,
            total: invalidFilters ? 0 : (list.data?.meta.total ?? 0),
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
            disabled: list.isPlaceholderData,
            showTotal: (total) => `${total} claims`,
            onChange: (nextPage, nextSize) => {
              setPage(nextSize === perPage ? nextPage : 1);
              setPerPage(nextSize);
            },
          }}
        />
      )}
    </>
  );
};
